import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Switch,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Link as LinkIcon } from '@mui/icons-material';
import { mcpService, HealthStatus, type AgentMcpServerAssignmentDto, type McpServerDto } from '../../services/mcpService';
import { useNotificationStore } from '../../store/notificationStore';

interface AgentMcpServerListProps {
  agentId: string;
  readOnly?: boolean;
}

const healthColor = (status: string): 'success' | 'error' | 'warning' | 'default' => {
  switch (status) {
    case HealthStatus.Healthy:
      return 'success';
    case HealthStatus.Unhealthy:
      return 'error';
    case HealthStatus.Degraded:
      return 'warning';
    default:
      return 'default';
  }
};

/**
 * Lists MCP servers assigned to an agent with add, toggle enable, and remove.
 */
export const AgentMcpServerList = ({ agentId, readOnly = false }: AgentMcpServerListProps) => {
  const [assignments, setAssignments] = useState<AgentMcpServerAssignmentDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [availableServers, setAvailableServers] = useState<McpServerDto[]>([]);
  const [loadingAvailable, setLoadingAvailable] = useState(false);
  const [assigningId, setAssigningId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const { addNotification } = useNotificationStore();

  const loadAssignments = async () => {
    if (!agentId) return;
    setLoading(true);
    setError(null);
    try {
      const list = await mcpService.getAgentMcpServers(agentId);
      setAssignments(list);
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to load MCP servers';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAssignments();
  }, [agentId]);

  const loadAvailableServers = async () => {
    setLoadingAvailable(true);
    try {
      const result = await mcpService.getMcpServers({ pageSize: 100, isActive: true });
      const assignedIds = new Set(assignments.map((a) => a.mcpServerId));
      setAvailableServers(result.items.filter((s) => !assignedIds.has(s.id)));
    } catch {
      setAvailableServers([]);
    } finally {
      setLoadingAvailable(false);
    }
  };

  const openAddDialog = () => {
    setAddDialogOpen(true);
    loadAvailableServers();
  };

  const handleAssign = async (mcpServerId: string) => {
    setAssigningId(mcpServerId);
    try {
      await mcpService.assignMcpToAgent(agentId, { mcpServerId, isEnabled: true });
      addNotification('MCP server assigned', 'success');
      loadAssignments();
      loadAvailableServers();
    } catch (err: any) {
      addNotification(err.response?.data?.message || err.message || 'Failed to assign', 'error');
    } finally {
      setAssigningId(null);
    }
  };

  const handleToggleEnabled = async (assignment: AgentMcpServerAssignmentDto) => {
    setTogglingId(assignment.id);
    try {
      await mcpService.assignMcpToAgent(agentId, {
        mcpServerId: assignment.mcpServerId,
        isEnabled: !assignment.isEnabled,
      });
      addNotification(assignment.isEnabled ? 'MCP server disabled' : 'MCP server enabled', 'success');
      loadAssignments();
    } catch (err: any) {
      addNotification(err.response?.data?.message || err.message || 'Failed to update', 'error');
    } finally {
      setTogglingId(null);
    }
  };

  const handleRemove = async (assignmentId: string) => {
    if (!window.confirm('Remove this MCP server from the agent?')) return;
    setRemovingId(assignmentId);
    try {
      await mcpService.removeMcpFromAgent(agentId, assignmentId);
      addNotification('MCP server removed', 'success');
      loadAssignments();
      loadAvailableServers();
    } catch (err: any) {
      addNotification(err.response?.data?.message || err.message || 'Failed to remove', 'error');
    } finally {
      setRemovingId(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" fontWeight={600}>
          MCP Servers
        </Typography>
        {!readOnly && (
          <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={openAddDialog}>
            Add MCP Server
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {assignments.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No MCP servers assigned. Add an MCP server to give this agent access to its tools.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Server</TableCell>
              <TableCell>Status</TableCell>
              {!readOnly && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {assignments.map((a) => (
              <TableRow key={a.id}>
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {a.mcpServerName}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  {togglingId === a.id ? (
                    <CircularProgress size={20} />
                  ) : (
                    <Switch
                      size="small"
                      checked={a.isEnabled}
                      disabled={readOnly}
                      onChange={() => handleToggleEnabled(a)}
                    />
                  )}
                  <Chip
                    label={a.isEnabled ? 'Enabled' : 'Disabled'}
                    size="small"
                    color={a.isEnabled ? 'success' : 'default'}
                    sx={{ ml: 1 }}
                  />
                </TableCell>
                {!readOnly && (
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemove(a.id)}
                      disabled={removingId === a.id}
                      aria-label="Remove MCP server"
                    >
                      {removingId === a.id ? <CircularProgress size={20} /> : <DeleteIcon fontSize="small" />}
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add MCP Server</DialogTitle>
        <DialogContent>
          {loadingAvailable ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : availableServers.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No other MCP servers available to assign. Create an MCP server first.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Health</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {availableServers.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {s.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                        {s.serverUrl}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={s.healthStatus} color={healthColor(s.healthStatus)} size="small" />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={assigningId === s.id ? <CircularProgress size={16} /> : <LinkIcon />}
                        disabled={!!assigningId}
                        onClick={() => handleAssign(s.id)}
                      >
                        Assign
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
